import { ChevronRight } from "@mui/icons-material";
import { Box, Menu, styled } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import IndieSideMenuItem from "./IndieSideMenuItem";

// Styled Components
const ChevronRightStyled = styled(ChevronRight)(() => ({
    fontSize: 18,
}));

/**
 * IndieSidebarMenuExpansive - A specialized sidebar menu component
 * that renders a top-level button which opens a popup menu (expansive style).
 * 
 * Used primarily for items with many children that don't fit well in a standard accordion.
 */
const IndieSidebarMenuExpansive = memo(({
    NavItemButton,
    StyledText,
    item,
    iconStyle,
    activeRoute,
    compact,
    checkItemAuthorization
}) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [canHover, setCanHover] = useState(false);
    const timeoutRef = useRef(null);

    // Detect hover capability + viewport width
    useEffect(() => {
        const checkCapabilities = () => {
            const mediaQuery = window.matchMedia('(hover: hover)');
            const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isWide = window.innerWidth >= 1200; // Project standard 'lg' breakpoint
            
            // On desktop we want hover AND width. On mobile we want click.
            setCanHover(mediaQuery.matches && !isTouch && isWide);
        };

        checkCapabilities();

        const mediaQuery = window.matchMedia('(hover: hover)');
        mediaQuery.addEventListener('change', checkCapabilities);
        window.addEventListener('resize', checkCapabilities);
        
        return () => {
            mediaQuery.removeEventListener('change', checkCapabilities);
            window.removeEventListener('resize', checkCapabilities);
        };
    }, []);

    const isActive = useMemo(() => activeRoute(item.path), [activeRoute, item.path]);

    // Filter children once before rendering
    const authorizedChildren = useMemo(() => {
        return item.children?.filter(child => checkItemAuthorization(child)) || [];
    }, [item.children, checkItemAuthorization]);

    // Close menu on navigation
    useEffect(() => {
        setAnchorEl(null);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, [pathname]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const handleOpen = useCallback((e) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setAnchorEl(null);
    }, []);

    const handleMouseEnter = useCallback((e) => {
        if (canHover) handleOpen(e);
    }, [canHover, handleOpen]);

    const handleMouseLeave = useCallback(() => {
        if (canHover) {
            timeoutRef.current = setTimeout(() => {
                setAnchorEl(null);
            }, 200); // 200ms buffer to prevent flickering
        }
    }, [canHover]);

    const handleMenuEnter = useCallback(() => {
        if (canHover && timeoutRef.current) clearTimeout(timeoutRef.current);
    }, [canHover]);

    const handleClick = useCallback((e) => {
        if (!canHover) {
            setAnchorEl(anchorEl ? null : e.currentTarget);
        }
    }, [canHover, anchorEl]);

    if (authorizedChildren.length === 0) return null;

    return (
        <>
            <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <NavItemButton
                    id={item.name.replaceAll(".", "_")}
                    name={item.name.replaceAll(".", "_")}
                    active={isActive}
                    onClick={handleClick}
                    sx={{ pr: 1.5 }}
                >
                    <item.icon sx={iconStyle(isActive)} />

                    <StyledText active={isActive} compact={compact} sx={{ pl: "8px" }}>
                        {!compact && t(item.name)}
                    </StyledText>

                    <Box mx="auto" />

                    <ChevronRightStyled
                        color="disabled"
                        className="accordionArrow"
                        sx={{
                            transform: open ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease-in-out"
                        }}
                    />
                </NavItemButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                // Disable pointer events on the root Menu only on desktop to prevent flickering
                // On mobile, we need it auto to allow tapping outside to close
                sx={{ pointerEvents: canHover ? 'none' : 'auto' }}
                slotProps={{
                    paper: {
                        sx: { mt: 0, ml: 0.5, boxShadow: 3, pointerEvents: 'auto' },
                        onMouseEnter: handleMenuEnter,
                        onMouseLeave: handleMouseLeave
                    }
                }}
            >
                {authorizedChildren.map((menuItem, idx) => (
                    <IndieSideMenuItem
                        key={menuItem.name || menuItem.id || idx}
                        menuItem={menuItem}
                        t={t}
                        handleClose={handleClose}
                        pathname={pathname}
                        checkItemAuthorization={checkItemAuthorization}
                    />
                ))}
            </Menu>
        </>
    );
});

export default IndieSidebarMenuExpansive;